/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { BrandsModel } from '@pukka/api';
import { Badge, Button, Checkbox, Text } from '@pukka/ui-components';
import { twMerge } from 'tailwind-merge';

import { IBrandsFormState } from '../BrandsForm';

interface BrandRowProps {
  brand: BrandsModel.BrandDetail;
  checked: boolean;
  disableCheckbox: boolean;
  withCheckbox: boolean;
  onChangeCheckbox: (brand: BrandsModel.BrandDetail) => void;
  onClickEdit: IBrandsFormState['handleClickEditBrand'];
  onClickAssignParent: (brand: { id: number; name: string }, parent: { id: number; name: string }) => void;
}

function BrandRow({ brand, checked, disableCheckbox, withCheckbox, onChangeCheckbox, onClickEdit, onClickAssignParent }: BrandRowProps) {
  const { t } = useTranslation('library');

  const { id, name, principal_id: parentId, principal_name: parentName, item_count: items } = brand || {};

  const isUnprincipalBrand = !parentId;

  const handleClickEdit = (e: React.MouseEvent) => {
    e.stopPropagation();

    onClickEdit(id, name, { id: parentId, name: parentName }, items);
  };

  const handleClickAssignParent = (e: React.MouseEvent) => {
    e.stopPropagation();

    onClickAssignParent({ id, name }, { id: parentId, name: parentName });
  };

  const handleChangeCheckbox = (e: React.MouseEvent) => {
    e.stopPropagation();

    onChangeCheckbox(brand);
  };

  return (
    <div
      key={id}
      id={`brand-${id}`}
      onClick={handleClickEdit}
      className={twMerge('flex h-14 cursor-pointer items-center hover:bg-neutral-50', checked ? 'bg-light-blue-50' : '')}
    >
      <div className="w-[72px]" />

      {withCheckbox && (
        <div className="px-6 py-4 pl-24">
          <Checkbox
            size="md"
            checked={checked}
            disabled={disableCheckbox}
            aria-disabled={disableCheckbox}
            data-testid={`brand-checkbox-${id}`}
            onClick={handleChangeCheckbox}
          />
        </div>
      )}

      <div className="group flex min-w-0 flex-1 items-center gap-1 px-4">
        <div className="truncate">
          <Text className="truncate text-neutral-700">{name}</Text>
          {!isUnprincipalBrand && (
            <Text variant="body-1" className="truncate text-neutral-400">
              {parentName} / {name}
            </Text>
          )}
        </div>

        <Button
          variant="nude-blue"
          size="s"
          icon={isUnprincipalBrand ? 'plus' : 'swap'}
          className="hidden border-none bg-transparent group-hover:block"
          onClick={handleClickAssignParent}
        >
          <Text className="truncate">{isUnprincipalBrand ? 'Assign principal to this brand' : 'Change principal of this brand'}</Text>
        </Button>

        <Button
          variant="nude-blue"
          size="s"
          icon="edit"
          className="hidden border-none bg-transparent group-hover:block"
          onClick={handleClickEdit}
        >
          <Text className="truncate">{t('brands.editBrand')}</Text>
        </Button>
      </div>
      <div className="px-4">
        <Badge color="green" type="invert" size="sm" text={t('brands.items', { count: items })} />
      </div>
    </div>
  );
}

export { BrandRow, type BrandRowProps };
